import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Stack,
    Tooltip,
    useTheme,
} from '@mui/material';
import {Add, ChatBubble, Delete, Person, SmartToy} from '@mui/icons-material';
import {DialogStep} from "../../models/DialogStep";
import {TextFieldWithHeader} from "../textField/TextFieldWithHeader";
import Typography from "@mui/material/Typography";
import {PrimaryIconButton, SecondaryIconButton} from "../button/ButtonVariants";
import {TextFieldChat} from "../textField/TextFieldChat";

type ExampleEditorProps = {
    exampleName: string;
    exampleNameError: boolean;
    exampleNameErrorMessage?: string;
    dialogSteps: DialogStep[];
    inputErrors: Record<number, { userRequest?: boolean; agentResponse?: boolean }>;
    onChangeExampleName: (value: string) => void;
    onAddDialogStep: () => void;
    onDeleteDialogStep: (index: number) => void;
    onUpdateDialogStep: (index: number, field: 'userRequest' | 'agentResponse', value: string) => void;
};

const ExampleEditor: React.FC<ExampleEditorProps> = (
    {
        exampleName,
        exampleNameError,
        exampleNameErrorMessage,
        dialogSteps,
        inputErrors,
        onChangeExampleName,
        onAddDialogStep,
        onDeleteDialogStep,
        onUpdateDialogStep,
    }) => {
    const theme = useTheme();

    return (
        <Box display="flex" flexDirection="column" height="100%" overflow={"hidden"} sx={{ padding: theme.spacing(4) }} gap={theme.spacing(2)}>
            <Typography variant={'explanation'}>
                Beispiele sind Musterunterhaltungen zwischen einem Endnutzer und einem Agenten.
                Sie helfen dem Agenten zu verstehen, wie er auf Fragen antworten soll.
            </Typography>
            <TextFieldWithHeader
                fullWidth
                required
                id="name-example-input"
                header="Anzeigename"
                error={exampleNameError}
                helperText={exampleNameErrorMessage}
                variant="outlined"
                value={exampleName}
                onChange={(e) => onChangeExampleName(e.target.value)}
            />

            <Box display={"flex"} flex={1} overflow="auto" gap={2} flexDirection={"column"}>
                <Stack spacing={2}>
                    {dialogSteps.map((step, index) => (
                        <Card key={index} variant="outlined">
                            <CardContent sx={{ paddingTop: '24px' }}>
                                <Box display={'flex'} gap={2} alignItems={'start'}>
                                    <PrimaryIconButton disabled>
                                        <ChatBubble />
                                    </PrimaryIconButton>
                                    <Box flex={1} display="flex" flexDirection="column" gap={2}>
                                        <TextFieldChat
                                            fullWidth
                                            placeholder="Gib einen Text für diesen Gesprächsbeitrag an"
                                            value={step.userRequest}
                                            onChange={(e) => onUpdateDialogStep(index, 'userRequest', e.target.value)}
                                            error={!!inputErrors[index]?.userRequest}
                                            helperText={inputErrors[index]?.userRequest ? 'Bitte geben Sie einen Text ein.' : undefined}
                                            startIcon={
                                                <Person color="secondary" fontSize="large"/>
                                            }
                                        />
                                        <TextFieldChat
                                            fullWidth
                                            placeholder="Gib einen Text für diesen Gesprächsbeitrag an"
                                            value={step.agentResponse}
                                            onChange={(e) => onUpdateDialogStep(index, 'agentResponse', e.target.value)}
                                            error={!!inputErrors[index]?.agentResponse}
                                            helperText={inputErrors[index]?.agentResponse ? 'Bitte geben Sie einen Text ein.' : undefined}
                                            startIcon={
                                                <SmartToy color="secondary" fontSize="large"/>
                                            }
                                        />
                                    </Box>
                                    <Tooltip title={dialogSteps.length === 1 ? "Minimum an Dialogfeldern erreicht" : "Dialogfeld löschen"}>
                                        <span>
                                            <PrimaryIconButton
                                                aria-label="löschen"
                                                disabled={dialogSteps.length === 1}
                                                onClick={() => onDeleteDialogStep(index)}
                                            >
                                            <Delete />
                                        </PrimaryIconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
                <Box display="flex" justifyContent="center">
                    <Tooltip title="Weiteres Dialogfeld erstellen">
                        <SecondaryIconButton
                            aria-label="Dialog hinzufügen"
                            size="large"
                            onClick={onAddDialogStep}
                        >
                            <Add sx={{ width: 18, height: 18, color: 'white' }} />
                        </SecondaryIconButton>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
};

export default ExampleEditor;